import React, { useEffect, useRef } from 'react';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';

/**
 * In-chat text search (WhatsApp-style): shown under the chat header, counts
 * matching messages and navigates between them (highlighting is applied by
 * the message renderer via the shared searchTerm).
 */
const ChatSearchBar = ({
  query,
  onQueryChange,
  matchCount,
  activeIndex,
  onNavigate, // (direction: 1 | -1)
  onClose,
  c,
  t,
  isRTL,
  theme,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'Enter') onNavigate(e.shiftKey ? -1 : 1);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 16px',
        borderBottom: `1px solid ${c.border}`,
        background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc',
        flexShrink: 0,
      }}
    >
      <div style={{ position: 'relative', flex: 1 }}>
        <Search size={16} style={{ position: 'absolute', insetInlineStart: '12px', top: '50%', transform: 'translateY(-50%)', color: c.textSecondary }} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t.searchInChatPlaceholder}
          style={{
            width: '100%',
            padding: '9px 12px',
            paddingInlineStart: '36px',
            borderRadius: '12px',
            border: `1px solid ${c.border}`,
            background: c.inputBg,
            color: c.text,
            outline: 'none',
            fontSize: '13.5px',
          }}
        />
      </div>

      <span style={{ fontSize: '12.5px', color: c.textSecondary, fontWeight: '600', whiteSpace: 'nowrap', minWidth: '52px', textAlign: 'center', direction: 'ltr' }}>
        {query ? (matchCount > 0 ? `${activeIndex + 1} / ${matchCount}` : t.noMatches) : ''}
      </span>

      <button
        type="button"
        onClick={() => onNavigate(-1)}
        disabled={matchCount === 0}
        title={t.prevMatch}
        aria-label={t.prevMatch}
        style={{
          width: '32px', height: '32px', borderRadius: '10px', border: `1px solid ${c.border}`,
          background: c.inputBg, color: c.textSecondary, display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: matchCount ? 'pointer' : 'not-allowed', opacity: matchCount ? 1 : 0.5,
        }}
      >
        <ChevronUp size={16} />
      </button>
      <button
        type="button"
        onClick={() => onNavigate(1)}
        disabled={matchCount === 0}
        title={t.nextMatch}
        aria-label={t.nextMatch}
        style={{
          width: '32px', height: '32px', borderRadius: '10px', border: `1px solid ${c.border}`,
          background: c.inputBg, color: c.textSecondary, display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: matchCount ? 'pointer' : 'not-allowed', opacity: matchCount ? 1 : 0.5,
        }}
      >
        <ChevronDown size={16} />
      </button>
      <button
        type="button"
        onClick={onClose}
        title={t.cancel}
        aria-label={t.cancel}
        style={{
          width: '32px', height: '32px', borderRadius: '10px', border: 'none',
          background: 'transparent', color: c.textSecondary, display: 'flex',
          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default ChatSearchBar;
